/**
 * One registry for every family's variants, replacing one React context per
 * family.
 *
 * Eighteen families each carrying their own context costs eighteen context
 * files, eighteen nested providers on the host page and thirty-six props on
 * the tweak panel. Here a family contributes one entry to
 * `CRESULTCARD_VARIANT_REGISTRY`, one provider holds every selection, and the
 * panel maps the registry to its groups in a single expression — nested by
 * family, so eighteen flat groups do not swamp it.
 *
 * The enum VALUE of a variant is its display label, because the tweak panel
 * persists by value. Both live exemplars already do this, and it is why they
 * list cleanly.
 */
"use client";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

/** The four groupings the eighteen pillars fall into, as the panel shows them. */
export enum CresultcardVariantFamily {
  PlaceAndStay = "Place & stay",
  MediaAndCulture = "Media & culture",
  DataAndDocuments = "Data & documents",
  CommerceAndWork = "Commerce & work",
}

export interface CresultcardVariantGroup {
  /** The persisted key. Stable forever: the panel stores selections under it. */
  readonly key: string;
  readonly family: CresultcardVariantFamily;
  readonly label: string;
  readonly options: readonly string[];
  readonly defaultValue: string;
}

/**
 * Every registered family. A pillar adds one line here and the tweak panel
 * picks it up; there is no second place to edit.
 */
export const CRESULTCARD_VARIANT_REGISTRY: readonly CresultcardVariantGroup[] =
  [];

type CresultcardVariantValues = Readonly<Record<string, string>>;

interface CresultcardVariantRegistryState {
  readonly values: CresultcardVariantValues;
  readonly select: (input: CresultcardVariantSelectInput) => void;
}

export interface CresultcardVariantSelectInput {
  readonly key: string;
  readonly value: string;
}

function defaultValuesOf(
  groups: readonly CresultcardVariantGroup[],
): CresultcardVariantValues {
  return groups.reduce<Record<string, string>>((values, group) => {
    values[group.key] = group.defaultValue;
    return values;
  }, {});
}

const CresultcardVariantContext =
  createContext<CresultcardVariantRegistryState | null>(null);

interface CresultcardVariantProviderProps {
  readonly children: ReactNode;
  /**
   * Defaults to the whole registry. A host that renders one family only can
   * pass that family's groups and keep its stored state that small.
   */
  readonly groups?: readonly CresultcardVariantGroup[];
}

export function CresultcardVariantProvider({
  children,
  groups = CRESULTCARD_VARIANT_REGISTRY,
}: CresultcardVariantProviderProps): ReactElement {
  const [values, setValues] = useState<CresultcardVariantValues>(() =>
    defaultValuesOf(groups),
  );

  const select = useCallback(
    ({ key, value }: CresultcardVariantSelectInput): void => {
      setValues((current) => ({ ...current, [key]: value }));
    },
    [],
  );

  const state = useMemo<CresultcardVariantRegistryState>(
    (): CresultcardVariantRegistryState => ({ values, select }),
    [select, values],
  );

  return (
    <CresultcardVariantContext.Provider value={state}>
      {children}
    </CresultcardVariantContext.Provider>
  );
}

/** Outside a provider every family renders its own recommended default. */
const EMPTY_REGISTRY_STATE: CresultcardVariantRegistryState = {
  values: {},
  select: (): void => undefined,
};

/**
 * The whole selection map, for the one consumer that needs it: the tweak
 * panel, which renders every group at once.
 */
export function useCresultcardVariantRegistry(): CresultcardVariantRegistryState {
  const state = useContext(CresultcardVariantContext);
  return state ?? EMPTY_REGISTRY_STATE;
}

export interface CresultcardVariantSelection<TVariant extends string> {
  readonly value: TVariant;
  readonly select: (value: TVariant) => void;
}

interface UseCresultcardVariantInput<TVariant extends string> {
  readonly group: CresultcardVariantGroup;
  /**
   * The family's own enum members, which is what narrows the stored string.
   *
   * A plain `readonly TVariant[]` on purpose: §7.7's template declares
   * `C_<PILLAR>_CARD_VARIANTS: readonly C<Pillar>cardVariant[]`, and asking
   * eighteen families to restate that as a non-empty tuple to satisfy a
   * fallback is a type talking about its own implementation. The empty case is
   * handled where it arises instead.
   */
  readonly options: readonly TVariant[];
}

/**
 * One family's current variant, typed as that family's enum. A stored value
 * that is no longer an option — a renamed variant, a stale persisted panel —
 * resolves to the recommended default rather than rendering nothing.
 *
 * The last resort is the first option. A family that declared its variants and
 * its group from the same list cannot reach it.
 */
export function useCresultcardVariant<TVariant extends string>({
  group,
  options,
}: UseCresultcardVariantInput<TVariant>): CresultcardVariantSelection<TVariant> {
  const { values, select } = useCresultcardVariantRegistry();
  const stored = values[group.key];
  const groupKey = group.key;
  const groupDefault = group.defaultValue;

  const value =
    options.find((option) => option === stored) ??
    options.find((option) => option === groupDefault) ??
    options[0];

  const selectVariant = useCallback(
    (next: TVariant): void => {
      select({ key: groupKey, value: next });
    },
    [groupKey, select],
  );

  return { value, select: selectVariant };
}
